<template>
  <div class="sp-page">
    <header class="sp-header">
      <div class="sp-header__inner">
        <p class="sp-brand">印章生产看板</p>
        <h1 class="sp-title">{{ supplierName || '供应商生产列表' }}</h1>
        <p class="sp-sub">无需登录 · 下拉刷新 · 可标记发货</p>
      </div>
    </header>

    <main class="sp-main">
      <div v-if="errorMsg" class="sp-error">{{ errorMsg }}</div>
      <div v-else-if="loading" class="sp-loading">加载中…</div>
      <template v-else>
        <section class="sp-section">
          <div class="sp-section__head">
            <h2>待处理</h2>
            <span class="sp-count">{{ activeList.length }}</span>
          </div>
          <p class="sp-hint">生产中、已发货优先展示，按时间正序</p>
          <div v-if="!activeList.length" class="sp-empty">暂无待生产/待收货需求</div>
          <article
            v-for="row in activeList"
            :key="'a-' + row.id"
            class="sp-card"
            :class="'is-' + String(row.status || '').toLowerCase()"
          >
            <div class="sp-card__top">
              <span class="sp-badge" :data-status="row.status">{{ row.statusLabel || statusLabel(row.status) }}</span>
              <time class="sp-time">{{ formatTime(row.createTime) }}</time>
            </div>
            <div class="sp-card__no">{{ row.demandNo }}</div>
            <ul class="sp-items">
              <li v-for="it in (row.items || [])" :key="it.id" class="sp-item">
                <div class="sp-item__imgs">
                  <img v-if="imgUrl(it.contactPointImage)" :src="imgUrl(it.contactPointImage)" alt="" class="sp-thumb" />
                  <img v-if="imgUrl(it.handleImage)" :src="imgUrl(it.handleImage)" alt="" class="sp-thumb" />
                </div>
                <div class="sp-item__meta">
                  <div class="sp-item__name">{{ it.ruleName || '印章' }}</div>
                  <div class="sp-item__sub">手柄：{{ it.handleName || '—' }} · 需求 <strong>{{ it.demandQty }}</strong> 件</div>
                </div>
              </li>
            </ul>
            <div v-if="row.remark" class="sp-remark">备注：{{ row.remark }}</div>
            <div class="sp-card__actions">
              <button
                v-if="row.status === 'PRODUCING'"
                type="button"
                class="sp-btn sp-btn--primary"
                :disabled="shippingId === row.id"
                @click="markShipped(row)"
              >
                {{ shippingId === row.id ? '提交中…' : '标记已发货' }}
              </button>
              <span v-else-if="row.status === 'SHIPPED'" class="sp-tip">已发货，等待对方确认收货</span>
            </div>
          </article>
        </section>

        <section class="sp-section sp-section--history">
          <div class="sp-section__head">
            <h2>已完成历史</h2>
            <span class="sp-count">{{ completedList.length }}</span>
          </div>
          <div v-if="!completedList.length" class="sp-empty">暂无已完成记录</div>
          <article
            v-for="row in completedList"
            :key="'c-' + row.id"
            class="sp-card is-completed"
          >
            <div class="sp-card__top">
              <span class="sp-badge" data-status="COMPLETED">已完成</span>
              <time class="sp-time">{{ formatTime(row.completedTime || row.createTime) }}</time>
            </div>
            <div class="sp-card__no">{{ row.demandNo }}</div>
            <ul class="sp-items">
              <li v-for="it in (row.items || [])" :key="it.id" class="sp-item">
                <div class="sp-item__meta">
                  <div class="sp-item__name">{{ it.ruleName || '印章' }} · {{ it.handleName || '—' }}</div>
                  <div class="sp-item__sub">需求 {{ it.demandQty }} · 实收 {{ it.receivedQty != null ? it.receivedQty : '—' }}</div>
                </div>
              </li>
            </ul>
          </article>
        </section>
      </template>
    </main>
  </div>
</template>

<script>
import { fetchPublicSealProduceList, shipPublicSealProduce } from '@/api/seal-produce-public'

export default {
  name: 'SealSupplierProducePublic',
  data() {
    return {
      token: '',
      loading: true,
      errorMsg: '',
      supplierName: '',
      activeList: [],
      completedList: [],
      shippingId: null
    }
  },
  created() {
    this.token = String((this.$route.query && this.$route.query.token) || '').trim()
    if (!this.token) {
      this.loading = false
      this.errorMsg = '链接无效：缺少访问参数 token'
      return
    }
    this.loadList()
  },
  methods: {
    statusLabel(code) {
      const map = { PRODUCING: '生产中', SHIPPED: '已发货', COMPLETED: '已完成' }
      return map[code] || code
    },
    formatTime(v) {
      if (!v) return ''
      const s = String(v).replace('T', ' ')
      return s.length > 16 ? s.slice(0, 16) : s
    },
    imgUrl(url) {
      if (!url) return ''
      const s = String(url).trim()
      if (/^https?:\/\//i.test(s) || s.startsWith('//') || s.startsWith('data:')) return s
      if (s.indexOf('/upload/') === 0 || s.indexOf('upload/') === 0) {
        return `https://wvxvb.cn/upload/${s.replace(/^\/?upload\/?/, '')}`
      }
      return s
    },
    loadList() {
      this.loading = true
      this.errorMsg = ''
      fetchPublicSealProduceList(this.token)
        .then((data) => {
          this.supplierName = (data && data.supplierName) || ''
          this.activeList = (data && data.active) || []
          this.completedList = (data && data.completed) || []
        })
        .catch((e) => {
          this.errorMsg = (e && e.message) || '加载失败'
          this.activeList = []
          this.completedList = []
        })
        .finally(() => {
          this.loading = false
        })
    },
    markShipped(row) {
      if (!row || !row.id) return
      if (!window.confirm('确认已发货？')) return
      this.shippingId = row.id
      shipPublicSealProduce(row.id, this.token)
        .then(() => {
          this.loadList()
        })
        .catch((e) => {
          window.alert((e && e.message) || '操作失败')
        })
        .finally(() => {
          this.shippingId = null
        })
    }
  }
}
</script>

<style scoped>
.sp-page {
  --bg: #f3f1ec;
  --ink: #1c2429;
  --muted: #6b7378;
  --card: #fffdf8;
  --line: #e4ddd2;
  --accent: #0f6e56;
  --accent-soft: #d8efe7;
  --warn: #b45309;
  --warn-soft: #fff1de;
  --ship: #1d4ed8;
  --ship-soft: #e8eefc;
  --done: #64748b;
  min-height: 100vh;
  background:
    radial-gradient(1200px 400px at 10% -10%, #dfece7 0%, transparent 55%),
    radial-gradient(900px 360px at 100% 0%, #efe6d8 0%, transparent 50%),
    var(--bg);
  color: var(--ink);
  font-family: "Segoe UI", "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif;
}

.sp-header {
  padding: 28px 16px 12px;
}

.sp-header__inner {
  max-width: 720px;
  margin: 0 auto;
}

.sp-brand {
  margin: 0;
  font-size: 12px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--accent);
  font-weight: 700;
}

.sp-title {
  margin: 8px 0 4px;
  font-size: clamp(22px, 5vw, 32px);
  line-height: 1.2;
  font-weight: 760;
}

.sp-sub {
  margin: 0;
  color: var(--muted);
  font-size: 13px;
}

.sp-main {
  max-width: 720px;
  margin: 0 auto;
  padding: 8px 16px 40px;
}

.sp-section {
  margin-top: 18px;
}

.sp-section__head {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.sp-section__head h2 {
  margin: 0;
  font-size: 16px;
}

.sp-count {
  display: inline-flex;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 999px;
  align-items: center;
  justify-content: center;
  background: var(--accent-soft);
  color: var(--accent);
  font-size: 12px;
  font-weight: 700;
}

.sp-hint {
  margin: 4px 0 12px;
  color: var(--muted);
  font-size: 12px;
}

.sp-empty,
.sp-loading,
.sp-error {
  padding: 18px;
  border: 1px dashed var(--line);
  border-radius: 14px;
  background: rgba(255, 253, 248, 0.7);
  color: var(--muted);
  text-align: center;
}

.sp-error {
  border-style: solid;
  color: #b42318;
  background: #fff5f4;
}

.sp-card {
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: 16px;
  padding: 14px;
  margin-bottom: 12px;
  box-shadow: 0 8px 24px rgba(28, 36, 41, 0.04);
}

.sp-card__top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.sp-badge {
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  background: var(--warn-soft);
  color: var(--warn);
}

.sp-badge[data-status='SHIPPED'] {
  background: var(--ship-soft);
  color: var(--ship);
}

.sp-badge[data-status='COMPLETED'] {
  background: #eef2f6;
  color: var(--done);
}

.sp-time {
  color: var(--muted);
  font-size: 12px;
}

.sp-card__no {
  margin-top: 8px;
  font-size: 13px;
  color: var(--muted);
  font-variant-numeric: tabular-nums;
}

.sp-items {
  list-style: none;
  margin: 12px 0 0;
  padding: 0;
}

.sp-item {
  display: flex;
  gap: 10px;
  padding: 10px 0;
  border-top: 1px solid var(--line);
}

.sp-item:first-child {
  border-top: 0;
  padding-top: 0;
}

.sp-item__imgs {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}

.sp-thumb {
  width: 52px;
  height: 52px;
  object-fit: cover;
  border-radius: 10px;
  background: #f0ebe3;
  border: 1px solid var(--line);
}

.sp-item__name {
  font-size: 15px;
  font-weight: 650;
}

.sp-item__sub {
  margin-top: 4px;
  font-size: 13px;
  color: var(--muted);
}

.sp-item__sub strong {
  color: var(--ink);
  font-size: 16px;
}

.sp-remark {
  margin-top: 8px;
  font-size: 12px;
  color: var(--muted);
}

.sp-card__actions {
  margin-top: 12px;
}

.sp-btn {
  appearance: none;
  border: 0;
  border-radius: 12px;
  height: 42px;
  padding: 0 16px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  width: 100%;
}

.sp-btn--primary {
  background: var(--accent);
  color: #fff;
}

.sp-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.sp-tip {
  display: block;
  text-align: center;
  color: var(--ship);
  font-size: 13px;
  font-weight: 600;
}

.sp-section--history .sp-card {
  opacity: 0.92;
}

@media (min-width: 768px) {
  .sp-header {
    padding-top: 40px;
  }

  .sp-card {
    padding: 18px;
  }

  .sp-btn {
    width: auto;
    min-width: 160px;
  }

  .sp-card__actions {
    display: flex;
    justify-content: flex-end;
  }

  .sp-main {
    display: grid;
    grid-template-columns: 1fr;
    gap: 8px;
  }
}
</style>
